/* import __COLOCATED_TEMPLATE__ from './audience-search-modal-content.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { inject as service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type Session from 'embercom/services/session';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type AiContentSegment from 'embercom/models/ai-content-segment';
import type { PlaygroundSettings } from 'embercom/lib/fin-playground/settings';
import { type PreviewSpoofingSettings } from 'embercom/components/workflows/preview/preview-module';

interface Signature {
  Args: {
    modal: any;
    isOpen: boolean;
    settings: PlaygroundSettings | PreviewSpoofingSettings;
    onSelect: (audience: { id: string }) => void;
    onModalClose: () => void;
    onDirectSave?: () => void;
    showBackButton?: boolean;
  };
  Element: HTMLElement;
}

export default class AudienceSearchModalContent extends Component<Signature> {
  @service declare router: RouterService;
  @service declare session: Session;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare appService: AppService;
  @tracked isLoadingInitialResults = true;
  @tracked query = '';
  @tracked originalSelectedSegmentIds: string[] = [];

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    // Store the original selections to revert to on back
    this.originalSelectedSegmentIds = [...(this.args.settings.selectedContentSegmentIds || [])];
    this.loadAudiences();
  }

  get audiences() {
    return this.aiContentSegmentsService.segments.map((segment: AiContentSegment) => ({
      id: segment.id,
      name: segment.nameWithFallback,
    }));
  }

  get filteredAudiences() {
    let audiences = this.audiences;

    // Filter by search query if there is one
    if (this.query) {
      // Cache the lowercased query for better performance
      let lowerQuery = this.query.toLowerCase();
      audiences = audiences.filter((audience) => audience.name.toLowerCase().includes(lowerQuery));
    }

    // Sort so originally saved audiences appear first (and stay there)
    return audiences.sort((a, b) => {
      let aWasOriginallySaved = this.originalSelectedSegmentIds.includes(a.id);
      let bWasOriginallySaved = this.originalSelectedSegmentIds.includes(b.id);

      // If both were originally saved or both were not, maintain original order
      if (aWasOriginallySaved === bWasOriginallySaved) {
        return 0;
      }

      // Originally saved audiences come first and stay there
      return aWasOriginallySaved ? -1 : 1;
    });
  }

  get noResults() {
    return !this.isLoadingInitialResults && this.filteredAudiences.length === 0;
  }

  get selectedAudienceIds() {
    return this.args.settings.selectedContentSegmentIds;
  }

  get hasSelectedAudiences() {
    return this.selectedAudienceIds.length > 0;
  }

  get hasChanges() {
    let currentIds = this.selectedAudienceIds.slice().sort();
    let originalIds = this.originalSelectedSegmentIds.slice().sort();

    // if no audiences are currently selected, disable the Apply button
    if (currentIds.length === 0) {
      return false;
    }

    if (currentIds.length !== originalIds.length) {
      return true;
    }

    return currentIds.some((id, index) => id !== originalIds[index]);
  }

  @action
  onInput(event: Event & { target: HTMLInputElement }) {
    this.query = event.target.value;
  }

  @action
  async loadAudiences() {
    this.isLoadingInitialResults = true;

    // Load segments if they haven't been loaded yet
    if (!this.aiContentSegmentsService.hasLoadedSegments) {
      await this.aiContentSegmentsService.loadSegments();
    }

    this.isLoadingInitialResults = false;
  }

  @action
  selectAudience(audience: { id: string }) {
    // Call the parent's onSelect which will handle the toggle logic
    this.args.onSelect(audience);
  }

  @action
  openAudienceManagement() {
    let route = this.appService.app.canUseMultiBrandGA
      ? 'apps.app.automation.fin-ai-agent.settings.audiences'
      : 'apps.app.settings.data.audiences';

    let url = this.router.urlFor(route, this.session.workspace.id);
    safeWindowOpen(url, '_blank');
  }

  @action
  handleSave() {
    // If we have onDirectSave callback, use it (skip settings modal)
    if (this.args.onDirectSave) {
      this.args.onDirectSave();
    }
    // Always call onModalClose to handle modal closing logic
    this.args.onModalClose();
  }

  get isDirectMode() {
    // If we have onDirectSave, it means we're in direct mode from inline dropdown
    return Boolean(this.args.onDirectSave);
  }

  @action
  onBack() {
    // if no audiences are currently selected, save that empty state
    if (this.selectedAudienceIds.length === 0) {
      // Keep the current empty selection and disable audience targeting
      this.args.settings.selectedContentSegmentIds.clear();
      if ('usingAudienceTargeting' in this.args.settings) {
        this.args.settings.usingAudienceTargeting = false;
      }
    } else {
      // Revert to original selections when going back
      this.args.settings.selectedContentSegmentIds.clear();
      this.args.settings.selectedContentSegmentIds.pushObjects(this.originalSelectedSegmentIds);

      // Reset usingAudienceTargeting based on whether we have any selected audiences
      if ('usingAudienceTargeting' in this.args.settings) {
        this.args.settings.usingAudienceTargeting = this.originalSelectedSegmentIds.length > 0;
      }
    }

    this.args.onModalClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::Modals::AudienceSearchModalContent': typeof AudienceSearchModalContent;
    'ai-agent/playground/modals/audience-search-modal-content': typeof AudienceSearchModalContent;
  }
}
